import { WORKFLOW_DETAIL_TODO_STATUSES } from "@performyard/service-contracts";
import type { WorkflowDetailTodoStatus, WorkflowDetailTodoStatusFilter } from "@performyard/service-contracts";

/**
 * Single source of truth for how each detail-page to-do status is presented.
 * Keyed by the contract's status union, so adding a status there is a compile
 * error until it is given a label, a chip color and a bar color here.
 *
 * `awaiting_signatures` keeps the "In Progress" label the design calls for; the
 * wire value is deliberately narrower (see WORKFLOW_DETAIL_TODO_STATUSES).
 */
export const DETAIL_TODO_STATUS_CONFIG: Record<
	WorkflowDetailTodoStatus,
	{
		label: string;
		chipColor: "neutral" | "green" | "red" | "primary";
		barClassName: string;
		/** Position in the filter panel's radio list. */
		filterOrder: number;
	}
> = {
	awaiting_signatures: { label: "In Progress", chipColor: "primary", barClassName: "bg-primary-5", filterOrder: 1 },
	completed: { label: "Complete", chipColor: "green", barClassName: "bg-green-5", filterOrder: 2 },
	overdue: { label: "Overdue", chipColor: "red", barClassName: "bg-red-5", filterOrder: 3 },
	incomplete: { label: "Incomplete", chipColor: "neutral", barClassName: "bg-neutral-2", filterOrder: 0 },
};

// Derived from the contract's status list and the config above rather than
// hand-listed, so a filter option can't go missing and its label always matches
// the table chip and the progress-bar legend.
export const DETAIL_TODO_STATUS_OPTIONS: readonly { label: string; value: "any" | WorkflowDetailTodoStatusFilter }[] = [
	{ label: "Any", value: "any" },
	...[...WORKFLOW_DETAIL_TODO_STATUSES]
		.sort((a, b) => DETAIL_TODO_STATUS_CONFIG[a].filterOrder - DETAIL_TODO_STATUS_CONFIG[b].filterOrder)
		.map((status) => ({ label: DETAIL_TODO_STATUS_CONFIG[status].label, value: status })),
];
